import React, {
  createContext,
  FC,
  ReactNode,
  useContext,
  useMemo,
} from "react";
import { Chat } from "stream-chat-react";
import { useChatClient } from "@/components/pages/chats/hooks/useChatClient";
import { getStreamChatSingleton } from "@/components/pages/chats/StreamChatClientManager";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { isHotelScopedSubscriptionRole } from "@/utils/accessControl";
import { useSelectedHotelId } from "@/utils/hotelContext";
import "stream-chat-react/dist/css/v2/index.css";

type StreamChatSessionValue = {
  streamUserId: string | null;
  streamSessionReady: boolean;
};

const StreamChatSessionContext = createContext<StreamChatSessionValue>({
  streamUserId: null,
  streamSessionReady: false,
});

export const useStreamChatSession = (): StreamChatSessionValue =>
  useContext(StreamChatSessionContext);

interface Props {
  children: ReactNode;
}

export const ChatProvider: FC<Props> = ({ children }) => {
  const client = useMemo(() => getStreamChatSingleton(), []);
  const { userProfile, isLoading: isAccessLoading } = useAuthenticatedUser();
  const selectedHotelId = useSelectedHotelId();
  const hotelId = selectedHotelId ?? userProfile?.profile?.hotelId ?? null;
  const { capabilities } = useAccessCapabilities();
  const isHotelScopedRole = isHotelScopedSubscriptionRole(userProfile);
  const isGuestChatEnabled =
    !isAccessLoading &&
    isHotelScopedRole &&
    capabilities.canUseGuestCommunication;
  const { userId, isLoading } = useChatClient(hotelId, isGuestChatEnabled);

  const session = useMemo<StreamChatSessionValue>(
    () => ({
      streamUserId: isGuestChatEnabled ? userId : null,
      streamSessionReady: isGuestChatEnabled && Boolean(userId) && !isLoading,
    }),
    [isGuestChatEnabled, userId, isLoading],
  );

  return (
    <StreamChatSessionContext.Provider value={session}>
      <Chat client={client} key={userId ?? "pending"}>
        {children}
      </Chat>
    </StreamChatSessionContext.Provider>
  );
};
